import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, Crown, Users, Sparkles, RefreshCw, LifeBuoy } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

const relativeDays = (s) => {
  if (!s) return null;
  const d = (Date.now() - new Date(s).getTime()) / 86400000;
  if (d < 1) return "Today";
  if (d < 2) return "Yesterday";
  if (d < 7) return `${Math.floor(d)}d ago`;
  return null;
};

const INTERVAL_TONE = {
  monthly: "bg-sky-50 text-sky-800 border-sky-200",
  yearly: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function AdminMembersTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [interval, setIntervalFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/members");
      setData(data);
    } catch {
      setData({ summary: {}, members: [] });
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setTimeout(() => setRefreshing(false), 400);
  };

  if (!data) return <div className="p-10 text-[var(--ff-text-muted)]" data-testid="admin-members-loading">Loading…</div>;

  const s = data.summary || {};
  const members = (data.members || []).filter((m) => {
    if (interval && m.interval !== interval) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (m.name || "").toLowerCase().includes(needle) || (m.email || "").toLowerCase().includes(needle);
  });

  const stats = [
    { key: "total", label: "Current members", value: s.total ?? 0, icon: Users },
    { key: "new_this_week", label: "New this week", value: s.new_this_week ?? 0, icon: Sparkles, accent: true },
    { key: "new_this_month", label: "New this month", value: s.new_this_month ?? 0, icon: Sparkles },
    { key: "founding", label: "Founding members", value: s.founding ?? 0, icon: Crown },
  ];

  return (
    <div className="space-y-6" data-testid="admin-members-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Crown size={22} className="text-[var(--ff-secondary)]" />
          <div>
            <p className="overline">Membership roster</p>
            <h2 className="font-display text-3xl">Current Members</h2>
          </div>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center gap-2 rounded-full border border-[var(--ff-border)] px-4 py-2 text-sm font-semibold hover:border-[var(--ff-primary)] transition-colors"
          data-testid="members-refresh"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="members-summary">
        {stats.map((st) => (
          <div
            key={st.key}
            className={`card-flat p-4 ${st.accent ? "ring-1 ring-[var(--ff-secondary)]/40" : ""}`}
            data-testid={`members-stat-${st.key}`}
          >
            <div className="flex items-center gap-2 text-[var(--ff-text-muted)]">
              <st.icon size={14} />
              <p className="overline">{st.label}</p>
            </div>
            <p className="font-display text-3xl mt-1" data-testid={`members-stat-value-${st.key}`}>{st.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: "", label: "All plans" },
          { key: "monthly", label: `Monthly (${s.monthly ?? 0})` },
          { key: "yearly", label: `Yearly (${s.yearly ?? 0})` },
        ].map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setIntervalFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
              interval === f.key
                ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]"
                : "bg-white text-[var(--ff-text-secondary)] border-[var(--ff-border)] hover:border-[var(--ff-primary)]"
            }`}
            data-testid={`members-filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            className="w-full pl-9 py-2 rounded-full text-sm border border-[var(--ff-border)] bg-white focus:outline-none focus:border-[var(--ff-primary)]"
            data-testid="members-search"
          />
        </div>
      </div>

      <div className="card-flat overflow-hidden" data-testid="members-list">
        {members.length === 0 ? (
          <p className="p-8 text-center text-[var(--ff-text-muted)]" data-testid="members-empty">
            No members yet. New sign-ups will appear here automatically.
          </p>
        ) : (
          <div className="divide-y divide-[var(--ff-border)]">
            {members.map((m) => {
              const rel = relativeDays(m.started_at);
              return (
                <div key={m.user_id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`member-row-${m.user_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate" data-testid={`member-name-${m.user_id}`}>{m.name || "—"}</p>
                      {m.is_founding_member && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border border-amber-200 bg-amber-50 text-amber-800" data-testid={`member-founding-${m.user_id}`}>
                          <Crown size={11} /> Founding{m.founder_number ? ` #${m.founder_number}` : ""}
                        </span>
                      )}
                      {rel && (
                        <span className="px-2 py-0.5 text-[11px] rounded-full border border-[var(--ff-secondary)]/40 bg-[var(--ff-secondary)]/10 text-[var(--ff-secondary)] font-semibold">
                          {rel}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--ff-text-muted)] truncate">{m.email}</p>
                  </div>

                  <div className="text-right shrink-0">
                    <span className={`inline-block px-2.5 py-1 text-xs rounded-full border font-semibold ${INTERVAL_TONE[m.interval] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                      {m.plan_name || "Member"}{m.price ? ` · $${m.price}/${m.interval === "yearly" ? "yr" : "mo"}` : ""}
                    </span>
                    <p className="text-[11px] text-[var(--ff-text-muted)] mt-1" data-testid={`member-joined-${m.user_id}`}>
                      Joined {fmtDate(m.started_at)}
                      {m.renews_at ? ` · renews ${fmtDate(m.renews_at)}` : ""}
                    </p>
                  </div>

                  <Link
                    to={`/admin/users/${m.user_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ff-primary)] text-[var(--ff-primary)] hover:bg-[var(--ff-primary)] hover:text-white transition-colors px-3 py-1.5 text-xs font-medium shrink-0"
                    data-testid={`member-view-${m.user_id}`}
                  >
                    <LifeBuoy size={12} /> View
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
